import { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import DataTable from 'components/Common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import Button from 'components/Common/Button';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { GoPlus } from 'react-icons/go';
import { useDeleteAgencyTeam, useGetAgencyTeams } from 'apis/agency/team/hook';
import { TeamMember } from 'apis/agency/team/types';
import Pagination, { usePagination } from 'components/Common/Pagination';
import { titleCase } from '@/utils/helperFunctions';
import Search from '@/components/Common/Search';

export default function Team() {
  const navigate = useNavigate();
  const { currentPage, perPage, setPage, setPerPage } = usePagination();
  const [searchText, setSearchText] = useState<string | null>(null);

  const { data: teamMembers, isLoading: isTeamMembersLoading } = useGetAgencyTeams({
    page: currentPage,
    per_page: perPage,
    filter: { name_or_email_cont: searchText, account_status_eq: status }
  });

  const { mutate: deleteTeamMember } = useDeleteAgencyTeam();

  return (
    <>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography variant='h6' fontWeight={500}>
          Team
        </Typography>
        <Box display='flex' flexDirection='row' gap={2}>
          <Box width={{ xs: '100%', sm: 'auto' }} minWidth={{ xs: '100%', sm: 200, md: 400 }}>
            <Search
              placeholder='Search team'
              searchText={searchText}
              setSearchText={setSearchText}
              size='small'
              fullWidth
            />
          </Box>
          <Button onClick={() => navigate('add')} variant='contained' color='primary' startIcon={<GoPlus />}>
            Add Member
          </Button>
        </Box>
      </Box>
      <DataTable
        data={teamMembers?.data}
        columns={columns(deleteTeamMember)}
        isHeaderSticky={true}
        isLoading={isTeamMembersLoading}
      />
      <Pagination
        totalPages={teamMembers?.meta?.total_pages}
        totalCount={teamMembers?.meta?.total_count ?? 0}
        currentPage={currentPage}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />
      <Outlet />
    </>
  );
}

type DeleteTeamMemberFn = (id: string) => void;

const columns = (deleteTeamMember: DeleteTeamMemberFn): ColumnDef<TeamMember>[] => [
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: 'Email',
    accessorKey: 'email'
  },
  {
    header: 'Phone',
    accessorKey: 'phone_number'
  },
  {
    header: 'Role',
    accessorKey: 'role',
    cell: (info: any) => titleCase(info.getValue())
  },
  {
    header: 'Action',
    cell: (info) => (
      <Box display='flex' gap={1}>
        <Link to={String(info.row.original.id)}>
          <IconButton>
            <MdOutlineEdit />
          </IconButton>
        </Link>
        <IconButton onClick={() => deleteTeamMember(String(info.row.original.id))} color='error'>
          <RiDeleteBin6Line style={{ color: 'red' }} />
        </IconButton>
      </Box>
    )
  }
];
